import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Json } from "@/integrations/supabase/types";

export interface Restaurant {
  id: string;
  owner_id: string;
  name: string;
  description: string | null;
  cuisine_type: string;
  address: string;
  city: string;
  postal_code: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  opening_hours: Json | null;
  image_url: string | null;
  image_urls: string[] | null;
  logo_url: string | null;
  about_us: string | null;
  is_verified: boolean;
  is_active: boolean;
  platform_fee_cents: number;
  total_deals_sold: number;
  total_revenue_cents: number;
  average_rating: number;
  review_count: number;
  menu_url?: string | null;
  menu_type?: string | null;
  table_count: number | null;
  seat_count: number | null;
  staff_full_capacity: number | null;
  offers_delivery: boolean | null;
  live_occupancy_percent: number | null;
  live_occupancy_updated_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useRestaurant(overrideId?: string | null) {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setRestaurant(null);
      setLoading(false);
      return;
    }

    fetchRestaurant();
  }, [user, overrideId]);

  const fetchRestaurant = async () => {
    if (!user) return;

    setLoading(true);

    if (overrideId) {
      // Admin viewing a specific restaurant
      const { data, error: err } = await supabase
        .from("restaurants")
        .select("*")
        .eq("id", overrideId)
        .single();

      if (err) {
        setError(err.message);
      } else {
        setRestaurant(data as Restaurant | null);
      }
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .rpc("get_my_restaurant");

    if (error) {
      setError(error.message);
    } else {
      const restaurant = Array.isArray(data) && data.length > 0 ? data[0] : null;
      setRestaurant(restaurant as Restaurant | null);
    }
    setLoading(false);
  };

  const createRestaurant = async (restaurantData: Omit<Partial<Restaurant>, 'id' | 'owner_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return { error: "Not authenticated" };

    const { data, error } = await supabase
      .from("restaurants")
      .insert({
        owner_id: user.id,
        name: restaurantData.name!,
        cuisine_type: restaurantData.cuisine_type!,
        address: restaurantData.address!,
        city: restaurantData.city!,
        postal_code: restaurantData.postal_code!,
        description: restaurantData.description,
        phone: restaurantData.phone,
        email: restaurantData.email,
        website: restaurantData.website,
      })
      .select()
      .single();

    if (error) {
      return { error: error.message };
    }

    setRestaurant(data as Restaurant);
    return { data };
  };

  const updateRestaurant = async (updates: Omit<Partial<Restaurant>, 'id' | 'owner_id' | 'created_at' | 'updated_at'>) => {
    if (!restaurant) return { error: "No restaurant found" };

    const { data, error } = await supabase
      .from("restaurants")
      .update(updates)
      .eq("id", restaurant.id)
      .select()
      .single();

    if (error) {
      return { error: error.message };
    }

    setRestaurant(data as Restaurant);
    return { data };
  };

  return {
    restaurant,
    loading,
    error,
    fetchRestaurant,
    createRestaurant,
    updateRestaurant,
  };
}
